import { html, raw } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { syncState } from '../lib/sync.js';
import { getProgress } from '../lib/store.js';

export const SPEAKERS = {
  krishna: { name: 'Krishna', initial: 'कृ', role: 'the teacher' },
  arjuna: { name: 'Arjuna', initial: 'अ', role: 'the student' },
  sanjaya: { name: 'Sanjaya', initial: 'स', role: 'the narrator' },
  dhritarashtra: { name: 'Dhritarashtra', initial: 'धृ', role: 'the blind king' },
  duryodhana: { name: 'Duryodhana', initial: 'दु', role: 'reported by Sanjaya' },
};

export function avatar(id, { size = '' } = {}) {
  const s = SPEAKERS[id];
  const initial = s ? s.initial : '?';
  return html`<span class="avatar${size ? ` avatar--${size}` : ''}${id === 'krishna' ? ' avatar--krishna' : ''}" aria-hidden="true">${initial}</span>`;
}

export function speakerChip(id, ref) {
  const s = SPEAKERS[id] || { name: id };
  return html`<span class="speaker">${avatar(id)}<span><span class="speaker__name">${s.name}</span>${ref ? html`<span class="speaker__ref"> · ${ref}</span>` : ''}</span></span>`;
}

// The logo: a sun rising over the horizon at dawn. Gradient ids are unique per
// instance: a duplicate id inside a hidden copy would blank every other copy.
let markId = 0;
export function brandMark(size = 30) {
  const id = `bm${++markId}`;
  return raw(`<svg class="brand__mark" width="${size}" height="${size}" viewBox="0 0 32 32" aria-hidden="true">
    <defs>
      <linearGradient id="${id}-bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#1b2150"/><stop offset="1" stop-color="#4a3576"/></linearGradient>
      <linearGradient id="${id}-sun" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffe2a6"/><stop offset="1" stop-color="#f08a3c"/></linearGradient>
    </defs>
    <rect width="32" height="32" rx="9" fill="url(#${id}-bg)"/>
    <path d="M8.5 21 a7.5 7.5 0 0 1 15 0 z" fill="url(#${id}-sun)"/>
    <path d="M6 22.6 h20" stroke="#ffd18a" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M16 7.6 v2.4 M9.4 10.4 l1.6 1.6 M22.6 10.4 l-1.6 1.6" stroke="#ffd18a" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`);
}

export function brand() {
  return html`<a class="brand" href="/" aria-label="Bhagavad Gita, home">${brandMark()}<span class="brand__word">Bhagavad Gita</span></a>`;
}

function initials(name) {
  const n = String(name || '').trim();
  return n ? n[0].toUpperCase() : '';
}

export function syncLabel(state = syncState()) {
  if (!state.user) return 'Saved on this device';
  switch (state.status) {
    case 'synced': return 'Synced';
    case 'syncing': return 'Syncing…';
    case 'offline': return 'Offline, saved on this device';
    case 'error': return 'Sync paused, retrying';
    default: return 'Signed in';
  }
}

function displayName() {
  const s = syncState();
  const p = getProgress().profile;
  return (s.user && s.user.name) || (p && p.name) || '';
}

export function profileButton({ className = '' } = {}) {
  const s = syncState();
  const name = displayName();
  const label = s.user ? `Account and settings. ${syncLabel(s)}.` : 'Settings and sync';
  const dot = s.user ? (s.status === 'synced' ? 'ok' : s.status === 'offline' || s.status === 'error' ? 'warn' : 'busy') : '';
  return html`<a class="icon-btn profile-btn ${className}" href="/settings" aria-label="${label}">
    ${s.user && initials(name) ? html`<span class="avatar avatar--user">${initials(name)}</span>` : icon('user', { size: 22 })}
    ${dot ? html`<span class="profile-btn__dot profile-btn__dot--${dot}" aria-hidden="true"></span>` : ''}
  </a>`;
}

// Top bar. `back` is a fallback path used when there is no in-app history.
export function topbar({ title = '', back = null, clear = false, trail = null, showBrand = false, alwaysTitle = false, search = true } = {}) {
  return html`<header class="topbar${clear ? ' topbar--clear' : ''}">
    <div class="topbar__inner wrap wrap--wide">
      ${back ? html`<div class="topbar__lead"><button type="button" class="icon-btn" data-back="${back}" aria-label="Back">${icon('arrow-left', { size: 22 })}</button></div>` : ''}
      ${showBrand ? html`<div class="topbar__brand only-mobile">${brand()}</div>` : ''}
      ${title ? html`<p class="topbar__title${alwaysTitle ? ' topbar__title--always' : ''}" aria-hidden="${alwaysTitle ? 'false' : 'true'}">${title}</p>` : ''}
      <div class="topbar__trail">
        ${trail || ''}
        ${search ? html`<a class="icon-btn" href="/search" aria-label="Search">${icon('search', { size: 22 })}</a>` : ''}
        ${profileButton({ className: 'only-mobile' })}
      </div>
    </div>
  </header>`;
}

const TABS = [
  { id: 'today', href: '/', label: 'Today', icon: 'home', match: ['today', 'welcome'] },
  { id: 'journey', href: '/journey', label: 'Journey', icon: 'journey', match: ['journey', 'chapter', 'read', 'verse', 'lesson'] },
  { id: 'explore', href: '/explore', label: 'Explore', icon: 'explore', match: ['explore', 'concepts', 'concept', 'people', 'person', 'search'] },
  { id: 'saved', href: '/saved', label: 'Saved', icon: 'bookmark', match: ['saved'] },
];

export function renderNav(routeName) {
  const nav = document.getElementById('nav');
  if (!nav) return;
  const s = syncState();
  const name = displayName();
  nav.innerHTML = String(html`
    <div class="nav__brand">${brand()}</div>
    <div class="nav__items">
      ${TABS.map((t) => html`<a class="nav__item" href="${t.href}"${t.match.includes(routeName) ? raw(' aria-current="page"') : ''}>
        <span class="nav__pill">${icon(t.icon, { size: 22 })}</span><span class="nav__label">${t.label}</span></a>`)}
    </div>
    <div class="nav__foot">
      <a class="nav__profile" href="/settings"${routeName === 'settings' ? raw(' aria-current="page"') : ''}>
        ${s.user && initials(name) ? html`<span class="avatar avatar--user">${initials(name)}</span>` : html`<span class="avatar">${icon('user', { size: 18 })}</span>`}
        <span class="grow">
          <span class="nav__profile-name">${s.user ? name || 'Your account' : 'Settings'}</span>
          <span class="nav__profile-sub">${syncLabel(s)}</span>
        </span>
        ${icon('settings', { size: 18 })}
      </a>
    </div>`);
}
